import { useToken } from '@chakra-ui/react';
import {
  Box,
  Text,
  Heading,
  Flex,
  useMediaQuery,
  theme,
} from '@everlywell/ui-kit';
import { CardsAccordionProps } from 'components/CardsAccordion';
import CardsCarouselAccordion from 'components/CardsCarouselAccordion';
import { MultipleCardsSwiperProps } from 'components/MultipleCardsSwiper';
import BulletPointWithIcon, {
  BulletPointWithIconProps,
} from 'components/PageSections/components/BulletPointWithIcon';
import Container from 'components/PageSections/components/SectionContainer';
import React, { useRef, useState, useEffect, useCallback } from 'react';

export type ProgramSectionProps = {
  title: string;
  description: string;
  bulletPoints?: BulletPointWithIconProps[];
  accordionCards?: CardsAccordionProps['items'];
  carouselCards?: MultipleCardsSwiperProps['cards'];
  backgroundColor?: string;
  disableAccordion?: boolean;
};

const ProgramSection = ({
  title,
  description,
  bulletPoints,
  accordionCards,
  carouselCards,
  backgroundColor,
  disableAccordion,
}: ProgramSectionProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [carouselXOffset, setCarouselXOffset] = useState(0);
  const [isTablet] = useMediaQuery(
    `(min-width: ${theme.breakpoints.tablet}) and (max-width: ${theme.breakpoints.desktop})`,
  );
  const [containerPaddingX] = useToken('space', ['4']);

  const updateCarouselXOffset = useCallback(() => {
    const containerXPosition =
      containerRef?.current?.getBoundingClientRect()?.x;

    setCarouselXOffset(containerXPosition ?? 0);
  }, [containerRef]);

  useEffect(() => {
    updateCarouselXOffset();
  }, [updateCarouselXOffset]);

  useEffect(() => {
    window.addEventListener('resize', updateCarouselXOffset);

    return () => {
      window.removeEventListener('resize', updateCarouselXOffset);
    };
  }, [containerRef, updateCarouselXOffset]);

  const carouselPaddingX = isTablet
    ? `calc(${carouselXOffset}px + ${containerPaddingX})`
    : carouselXOffset;

  return (
    <Flex
      width="100vw"
      paddingY={['10', '20', '20']}
      backgroundColor={backgroundColor ?? 'tints.white'}
      marginBottom={[-10, -10, -20]}
      as="section"
      flexDirection="column"
      gap={['10', '20', '20']}
    >
      <Container
        size={['fluid', null, 'xl']}
        alignSelf="center"
        centerContent
        as="div"
      >
        <Flex
          width="100%"
          justifyContent="space-between"
          flexDirection={['column', 'column', 'row']}
          paddingX={[4, 4, 0]}
          rowGap="6"
          ref={containerRef}
        >
          <Flex
            flexDirection="column"
            maxWidth="567px"
            columnGap="8"
            rowGap="6"
          >
            <Heading size="2xl">{title}</Heading>
            <Text size="lg">{description}</Text>
          </Flex>
          {bulletPoints && bulletPoints.length > 0 ? (
            <Flex
              display="flex"
              flexDirection="column"
              gap="2"
              role="list"
              alignSelf={['start', 'start', 'end']}
            >
              {bulletPoints?.map((bulletPoint, index) => (
                <BulletPointWithIcon
                  key={`bullet-${index}`}
                  text={bulletPoint.text || ''}
                  textColor={bulletPoint.textColor || 'tints.darkGrey'}
                  iconName={bulletPoint.iconName || 'CheckCircle'}
                  iconColor={bulletPoint.iconColor || 'viridian.base'}
                  weight="fill"
                />
              ))}
            </Flex>
          ) : null}
        </Flex>
      </Container>
      <Box paddingX={[4, 0, 0]}>
        {accordionCards && carouselCards && (
          <CardsCarouselAccordion
            accordionCards={accordionCards}
            carouselCards={carouselCards}
            carouselFadeBgColor={backgroundColor}
            carouselStyles={{
              paddingLeft: carouselPaddingX,
              paddingRight: carouselPaddingX,
            }}
            disableAccordion={disableAccordion}
          />
        )}
      </Box>
    </Flex>
  );
};

export default ProgramSection;
